import {
  observable,
  action,
  autorun,
  extendObservable,
  makeObservable,
  computed,
  ObservableMap
} from 'mobx'
import localStore from 'store'
import { isPaused } from '../hooks/use-debounced-props'
import type { IMapping } from './models/mapping'

type IClipboard = {
  mapping: IMapping
}

class UIStore {
  selectedItem = null
  multiSelectedItems = []
  mouseLabel = null
  hoveredItem = null
  messages = []
  metadata = null
  modalState = []
  dataSourceFactory = null
  easyConfigLastOpened = ''
  toastError = null
  shiftKeyUp = false
  isPreview = false
  clipboard: IClipboard = {} as IClipboard
  lastRefreshTime = null
  selectedItemRef = observable.box(null)
  previewUrl = null
  router = null
  selectedMapping = null
  preventCollision = false
  contentRoute = null
  selectedDevice = 'desktop'
  selectedDataSourceId = null
  drawerOpen = false
  localSettings = new Map() as ObservableMap
  componentProps = new Map() as ObservableMap
  editingTheme = null
  isPreviewingAssets = false
  layoutEditorScale = 1
  xRay = false
  gridRuler = false
  editorGridHeight = 0
  styleEditorTarget = null
  styleEditorCompId = ''
  styleEditorSubComps = []
  styleEditorClassEditCb = null
  WF = null
  constructor () {
    makeObservable(this, {
      localSettings: observable.shallow,
      componentProps: observable,
      multiSelectedItems: observable,
      selectedItem: observable,
      mouseLabel: observable,
      hoveredItem: observable,
      messages: observable,
      metadata: observable,
      modalState: observable,
      pushModalState: action,
      popModalState: action,
      dataSourceFactory: observable,
      easyConfigLastOpened: observable,
      propsPaneSettings: computed,
      toastError: observable,
      shiftKeyUp: observable,
      isPreview: observable,
      previewUrl: observable,
      router: observable,
      selectedMapping: observable,
      preventCollision: observable,
      contentRoute: observable,
      selectedDevice: observable,
      selectedDataSourceId: observable,
      drawerOpen: observable,
      clearSelectedItems: action,
      set: action,
      pauseMapper: action,
      propsMapperPaused: computed,
      refreshData: action,
      resumeMapper: action,
      togglePaused: action,
      setRouter: action,
      setSelectedDevice: action,
      setPreviewUrl: action,
      selectItem: action,
      selectItemRef: action,
      addRecentBinding: action,
      togglePreview: action,
      toggleXray: action,
      toggleGridRuler: action,
      setDataSourceFactory: action,
      addError: action,
      addInfo: action,
      clearMessages: action,
      updateSelected: action,
      togglePreventCollision: action,
      editTheme: action,
      editingTheme: observable.shallow,
      previewAssets: action,
      isPreviewingAssets: observable,
      layoutEditorScale: observable,
      setScale: action,
      xRay: observable,
      gridRuler: observable,
      editorGridHeight: observable,
      setEditorGridHeight: action,
      styleEditorTarget: observable,
      styleEditorCompId: observable,
      styleEditorSubComps: observable,
      styleEditorCommitRollback: action,
      WF: observable,
      setWF: action
    })

    if (typeof window !== 'undefined') {
      if ((window as any).is_pls_client) {
        extendObservable(this, {
          lastRefreshTime: null
        })
      }
      try {
        this.localSettings.replace(localStore.get('ph-settings'))
      } catch (e) { }
    }
  }

  set = (k, v) => {
    this[k] = v
  }
  
  setWF (wf) {
    this.WF = wf
  }

  setScale (n) {
    this.layoutEditorScale = n
  }

  get propsPaneSettings () {
    const isExpanded = this.localSettings.get('pane.expanded')
    return {
      isExpanded: isExpanded === true || isExpanded === undefined,
      split: this.localSettings.get('pane.position') === 'bottom'
        ? 'horizontal'
        : 'vertical',
      defaultSelectedTab: this.localSettings.get('pane.defaultSelectedTab') || 0,
      splitRatio: this.localSettings.get('pane.splitRatio') || 0.6
    }
  }

  get propsMapperPaused () {
    return isPaused.get()
  }

  pauseMapper = () => {
    isPaused.set(true)
  }

  refreshData = () => {
    if (!uistore.selectedItem) return
    const dataReload = true
    this.dataSourceFactory.loadData(uistore.selectedItem.completeFieldSet, dataReload)
    this.lastRefreshTime = new Date().getTime()
  }

  resumeMapper = () => {
    isPaused.set(false)
  }

  togglePaused = () => {
    isPaused.set(!isPaused.get())
  }

  setRouter (r) {
    const { match, location, history } = r
    this.router = { match, location, history }
  }

  setSelectedDevice (device) {
    this.selectedDevice = device
  }

  setPreviewUrl (url) {
    this.previewUrl = url
  }

  selectItemRef (ref) {
    this.selectedItemRef.set(ref)
  }

  clearSelectedItems () {
    this.selectedItem = null
    this.selectedItemRef.set(null);

    (this.multiSelectedItems as any)?.clear()
  }

  removeSelectedItems () {
    this.multiSelectedItems?.forEach?.(item => item.del())
  }

  copySelectedItemsToClipboard () {
    if (!this.multiSelectedItems.length) return;

    localStorage.setItem(
      'layout_clipboard', 
      JSON.stringify(this.multiSelectedItems.map(comp => comp.getSnapshotWithNewIds()))
    )
  }

  selectItem (item, event, ref?) {

    //selections with cmd/ctrl
    if (event.ctrlKey || event.metaKey) {
      this.selectedItem = null
      this.selectedItemRef.set(null);         
    }
    //single selection
    else {
      (this.multiSelectedItems as any).clear()
      this.editingTheme = null
      this.isPreviewingAssets = false
      this.selectedItem = item
      this.selectedItemRef.set(ref)
    }

    if (item.type !== 'Page') {
      this.multiSelectedItems.push(item)
    }
    
  }

  pushModalState (state) {
    this.modalState.push(state)
  }

  popModalState () {
    return this.modalState.pop()
  }

  get recentBindings () {
    return this.localSettings.get('recentBindings') || []
  }

  get appsListFormat () {
    return this.localSettings.get('appsListFormat') ?? 'list'
  }

  set appsListFormat (val: 'list' | 'tiles') {
    this.localSettings.set('appsListFormat', val)
  }

  addRecentBinding = b => {
    const rb = this.recentBindings || []
    const foundIndex = rb.findIndex(bb => b === bb)
    if (foundIndex !== -1) {
      rb.splice(foundIndex, 1)
    }
    rb.unshift(b)
    rb.length = 25
    this.localSettings.set('recentBindings', rb)
  }

  togglePreview = () => {
    this.isPreview = !this.isPreview
  }

  toggleXray = () => {
    this.xRay = !this.xRay
  }

  toggleGridRuler = () => {
    this.gridRuler = !this.gridRuler
  }

  setDataSourceFactory (f) {
    this.dataSourceFactory = f
    return this.dataSourceFactory
  }

  setEditorGridHeight = (height) => {
    this.editorGridHeight = height
  }

  addError = (msg: string) => {
    this.messages.push({ message: msg, intent: 3 })
  }

  addInfo = (msg: string) => {
    this.messages.push({ message: msg, intent: 1 })
  }

  clearMessages = () => {
    this.messages.length = 0
  }

  updateSelected = component => {
    this.selectedItem = component
  }

  togglePreventCollision = () => {
    this.preventCollision = !this.preventCollision
  }

  ds = (id: string) => {
    return this.dataSourceFactory && this.dataSourceFactory.getInstance(id)
  }

  editTheme (theme) {
    this.editingTheme = theme
    this.isPreviewingAssets = false
    this.clearSelectedItems()
  }

  previewAssets () {
    this.editingTheme = null
    this.clearSelectedItems()
    this.isPreviewingAssets = true;
  }

  get selectedTheme () {
    return this.localSettings.get('theme')
  }

  styleEditorCommitRollback = (editMap: Record<string, string>, commitRollback: 'commit'|'rollback') => {
    if (commitRollback === 'commit' && this.styleEditorClassEditCb) {
      this.styleEditorClassEditCb(editMap)
    }
    this.styleEditorCompId = ''
    this.styleEditorSubComps = []
    this.styleEditorTarget = null
  }
}
export type IUIStore = typeof UIStore

export let uistore: UIStore;
export const initUiStore = () => {
  if (uistore) return uistore
  uistore = new UIStore()
  if (typeof window !== 'undefined' && !process.env.IS_SERVER && !process.env.SSR) {
    import('../components/Toaster').then(({ initToaster }) => {
      initToaster(uistore);
    })
  }
  if(typeof window !== 'undefined') {
    (window as any).uistore = uistore
  }
  
  autorun(() => {
    const newSettings = uistore.localSettings.toJSON()
    if (newSettings) localStore.set('ph-settings', newSettings)
  })
  return uistore
}
